import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  endImpersonationSession,
  readImpersonation,
  type ImpersonationMarker,
} from '@/lib/impersonation-session';
import { awaitPendingSaves } from '@/stores/designStore';

/** Fixed height of the bar; layouts subtract it via the CSS variable. */
export const IMPERSONATION_BANNER_HEIGHT_PX = 40;
export const IMPERSONATION_BANNER_HEIGHT_VAR = '--impersonation-banner-height';

/** How often the remaining time is recomputed. */
const TICK_MS = 15_000;
/** The bar turns into a warning this long before the session ends. */
const WARNING_MS = 5 * 60_000;
/** Browsers fire longer timeouts at once; the interval covers the rest. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * The permanent notice of an impersonation tab: whose account this is, how
 * long the session still runs, and a way to end it. Rendered once above all
 * routes (App.tsx), so it also stands in the Creator. Renders nothing in an
 * ordinary tab and in a tab whose session already ended (App shows the end
 * screen there).
 *
 * While mounted it publishes its height as `--impersonation-banner-height`
 * on the root element; the full-height shells (MainLayout, SellerShell)
 * subtract it. Outside an impersonation tab the variable is absent and
 * their `0px` fallback leaves them unchanged.
 */
export function ImpersonationBanner() {
  const [marker] = useState<ImpersonationMarker | null>(() => {
    const current = readImpersonation();
    return current && !current.endedReason ? current : null;
  });

  if (!marker) return null;
  return <ActiveBanner marker={marker} />;
}

function ActiveBanner({ marker }: { marker: ImpersonationMarker }) {
  const { t } = useTranslation();
  const expiresAt = Date.parse(marker.expiresAt);
  const [now, setNow] = useState(() => Date.now());

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      IMPERSONATION_BANNER_HEIGHT_VAR,
      `${IMPERSONATION_BANNER_HEIGHT_PX}px`
    );
    return () => {
      root.style.removeProperty(IMPERSONATION_BANNER_HEIGHT_VAR);
    };
  }, []);

  useEffect(() => {
    const tick = () => {
      const current = Date.now();
      setNow(current);
      if (current >= expiresAt) {
        endImpersonationSession('expired');
      }
    };
    tick();
    const interval = window.setInterval(tick, TICK_MS);
    // A precise timer as well, so the end does not wait for the next tick.
    const timeout = window.setTimeout(
      tick,
      Math.min(MAX_TIMEOUT_MS, Math.max(0, expiresAt - Date.now()))
    );
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [expiresAt]);

  const remainingMs = Math.max(0, expiresAt - now);
  const remainingMinutes = Math.ceil(remainingMs / 60_000);
  const warning = remainingMs <= WARNING_MS;
  const account = marker.targetName
    ? `${marker.targetName} (${marker.targetEmail})`
    : marker.targetEmail;

  return (
    <div
      role="region"
      aria-label={t('seller.impersonation.banner.regionLabel')}
      data-state={warning ? 'warning' : 'active'}
      style={{ height: IMPERSONATION_BANNER_HEIGHT_PX }}
      className={cn(
        'fixed inset-x-0 top-0 z-[60] flex items-center gap-3 px-4 text-[13px] font-medium',
        warning ? 'bg-warning text-ink' : 'bg-ink text-paper'
      )}
    >
      <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 truncate">
        {t('seller.impersonation.banner.actingAs', { account })}
      </span>
      <span className="dc-mono shrink-0 tabular-nums" aria-live="polite">
        {t('seller.impersonation.banner.remaining', {
          count: remainingMinutes,
        })}
      </span>
      {warning && (
        <span className="hidden min-w-0 truncate font-semibold md:inline">
          {t('seller.impersonation.banner.endingSoon')}
        </span>
      )}
      <div className="flex-1" />
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-7 shrink-0 bg-paper text-ink hover:bg-paper-2"
        onClick={() => {
          // A save still in flight would hold the reload on the
          // unsaved-changes prompt; let it land first. Truly unsaved
          // changes still prompt, and cancelling keeps the session.
          void awaitPendingSaves().finally(() =>
            endImpersonationSession('ended')
          );
        }}
      >
        {t('seller.impersonation.banner.end')}
      </Button>
    </div>
  );
}
